import { ApiClient } from '../utils/apiClient';
import { logoutCleanupService } from './logoutCleanupService';
import { sessionHeartbeatService } from './sessionHeartbeatService';
import { setAuthRedirectMessage } from './sessionMigration';
import { secureTokenStorage } from '../utils/secureTokenStorage';
import { logger } from '../utils/logger';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  timestamp: number;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string | null;
  expiresIn: number;
  user: User;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  phoneCountryCode?: number;
  phoneNumber?: string;
  organization?: string;
  callSign?: string;
}

export interface User {
  id: number;
  username: string;
  email?: string;
  phoneNumber?: string;
  avatarUrl?: string;
  phoneCountryCodeId?: number;
  callSign?: string;
  status?: string;
}

class AuthService {
  private isHandlingUnauthorized = false;

  map401Message(message?: string): string {
    if (message?.includes('其他设备')) {
      return '账号已在其他设备登录';
    }
    if (message?.includes('会话已过期')) {
      return '会话已过期，请重新登录';
    }
    if (message?.includes('账户不可用')) {
      return '账户不可用，请联系管理员';
    }
    if (message?.includes('认证服务不可用')) {
      return '认证服务暂时不可用，请稍后重试';
    }
    return message || '请重新登录';
  }

  /**
   * 401 / 410：读 message → 提示 → 清存储 → 断 MQTT → 跳登录
   */
  async handleUnauthorized(serverMessage?: string): Promise<void> {
    if (this.isHandlingUnauthorized) {
      return;
    }
    this.isHandlingUnauthorized = true;

    try {
      const userMessage = this.map401Message(serverMessage);
      logger.warn('认证失效:', userMessage);

      setAuthRedirectMessage(userMessage);
      sessionHeartbeatService.stop();

      try {
        sessionStorage.setItem('wavelink_logged_out', 'true');
      } catch {
        // ignore
      }

      try {
        await logoutCleanupService.performLogoutCleanup({ force: true, silent: true });
      } catch (error) {
        logger.error('401 清理资源失败:', error);
      }

      this.clearAuth();

      window.dispatchEvent(
        new CustomEvent('auth-session-expired', { detail: { message: userMessage } })
      );
      window.dispatchEvent(new CustomEvent('auth-logout'));
    } finally {
      this.isHandlingUnauthorized = false;
    }
  }

  private getLoginFriendlyErrorMessage(error: unknown): string {
    const isProduction = process.env.NODE_ENV === 'production';
    const errorMessage = error instanceof Error ? error.message : String(error);

    if (isProduction) {
      logger.error('登录错误详情（仅记录）:', { message: errorMessage });
    }

    if (errorMessage.includes('NetworkError') || errorMessage.includes('fetch') || errorMessage.includes('Failed to fetch')) {
      return '网络连接失败，请检查网络设置后重试';
    }
    if (errorMessage.includes('HTTP error! status: 400') || errorMessage.includes('HTTP error! status: 401')) {
      return '用户名或密码错误，请检查后重试';
    }
    if (errorMessage.includes('HTTP error! status: 403') || errorMessage.includes('账户不可用')) {
      return '账户不可用，请联系管理员';
    }
    if (errorMessage.includes('HTTP error! status: 404') || errorMessage.includes('HTTP error! status: 502') || errorMessage.includes('HTTP error! status: 503')) {
      return '服务暂时不可用，请稍后重试';
    }
    if (errorMessage.includes('HTTP error! status: 500')) {
      return '服务器错误，请稍后重试';
    }
    if (errorMessage.includes('Redis 不可用')) {
      return '认证服务暂时不可用，请稍后重试';
    }
    if (errorMessage.includes('timeout')) {
      return '请求超时，请检查网络连接后重试';
    }

    return '登录失败，请稍后重试';
  }

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await ApiClient.post<ApiResponse<LoginResponse>>(
        '/users/login',
        credentials,
        { skipAuth: true, skip401Handler: true }
      );

      if (!response.success) {
        throw new Error(response.message || '登录失败');
      }

      const data = response.data;

      const userToStore = {
        ...data.user,
        id: data.user.id.toString(),
        email: data.user.email || '',
      };

      secureTokenStorage.setAccessToken(data.accessToken, data.expiresIn);
      localStorage.setItem('user', JSON.stringify(userToStore));

      try {
        sessionStorage.removeItem('wavelink_logged_out');
      } catch (error) {
        console.warn('清除登出标志失败:', error);
      }

      sessionHeartbeatService.start();
      window.dispatchEvent(new CustomEvent('auth-success'));

      return data;
    } catch (error) {
      logger.error('Login error:', error);
      throw new Error(this.getLoginFriendlyErrorMessage(error));
    }
  }

  async logout(): Promise<void> {
    try {
      const token = this.getToken();

      if (token) {
        try {
          await ApiClient.post<ApiResponse<string>>(
            '/users/logout',
            null,
            {
              headers: { Authorization: `Bearer ${token}` },
              skip401Handler: true,
            }
          );
        } catch {
          logger.debug('登出完成（本地清理）');
        }
      }
    } catch (error) {
      logger.error('登出过程中发生错误:', error);
    } finally {
      sessionHeartbeatService.stop();

      try {
        sessionStorage.setItem('wavelink_logged_out', 'true');
      } catch (error) {
        logger.warn('设置登出标志失败:', error);
      }

      window.dispatchEvent(new CustomEvent('auth-logout-start'));
      await new Promise((resolve) => setTimeout(resolve, 100));

      try {
        await logoutCleanupService.performLogoutCleanup({ force: true, silent: false });
      } catch (error) {
        logger.error('资源清理失败:', error);
      }

      try {
        this.clearAuth();
      } catch (error) {
        logger.error('清理本地存储失败:', error);
      }

      setAuthRedirectMessage('已安全退出');
      window.dispatchEvent(new CustomEvent('auth-logout'));
    }
  }

  getToken(): string | null {
    return secureTokenStorage.getAccessToken();
  }

  getSessionExpiresAt(): number | null {
    return secureTokenStorage.getSessionExpiresAt();
  }

  clearAuth(): void {
    secureTokenStorage.clearTokens();
    localStorage.removeItem('user');
  }

  getUser(): User | null {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return null;

      const storedUser = JSON.parse(userStr);

      if (!storedUser || !storedUser.id || !storedUser.username) {
        logger.warn('用户信息格式不正确，清除无效数据');
        localStorage.removeItem('user');
        return null;
      }

      return {
        ...storedUser,
        id: parseInt(storedUser.id, 10),
        email: storedUser.email || undefined,
      };
    } catch (error) {
      logger.error('解析用户信息失败:', error);
      localStorage.removeItem('user');
      return null;
    }
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  private getUserFriendlyErrorMessage(error: unknown): string {
    const errorMessage = error instanceof Error ? error.message : String(error);

    if (errorMessage.includes('NetworkError') || errorMessage.includes('fetch') || errorMessage.includes('Failed to fetch')) {
      return '网络连接失败，请检查网络设置后重试';
    }
    if (errorMessage.includes('HTTP error! status: 400')) {
      return '请求参数错误，请检查输入信息';
    }
    if (errorMessage.includes('HTTP error! status: 401')) {
      return '认证失败，请重新登录';
    }
    if (errorMessage.includes('HTTP error! status: 403')) {
      return '权限不足，无法执行此操作';
    }
    if (errorMessage.includes('HTTP error! status: 404') || errorMessage.includes('HTTP error! status: 502') || errorMessage.includes('HTTP error! status: 503')) {
      return '服务暂时不可用，请稍后重试';
    }
    if (errorMessage.includes('HTTP error! status: 500')) {
      return '服务器错误，请稍后重试';
    }
    if (errorMessage.includes('username') && errorMessage.includes('already exists')) {
      return '用户名已存在，请选择其他用户名';
    }
    if (errorMessage.includes('email') && errorMessage.includes('already exists')) {
      return '邮箱已被注册，请使用其他邮箱';
    }
    if (errorMessage.includes('timeout')) {
      return '请求超时，请检查网络连接后重试';
    }

    return '注册失败，请稍后重试';
  }

  async register(userData: RegisterRequest): Promise<void> {
    try {
      const response = await ApiClient.post<ApiResponse<{ success: boolean; message?: string }>>(
        '/users/register',
        userData,
        { skipAuth: true, skip401Handler: true }
      );

      if (!response.success) {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error) {
      logger.error('Registration error:', error);
      throw new Error(this.getUserFriendlyErrorMessage(error));
    }
  }
}

export const authService = new AuthService();
